import { useState, useEffect } from 'react';
import axios from 'axios';
import { logger } from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import useTrialExpirationHandler from './useTrialExpirationHandler';
import { useSubscriptionExpirationHandler } from './useSubscriptionExpirationHandler';

interface OrganizationInfo {
  plan: string;
  monthlySimQuota?: number;
  simUsed?: number;
  status?: string;
  nextBillingAt?: string;
  [key: string]: any; // Allow for additional properties
}

const useOrganizationInfo = () => {
  const { user } = useAuth();
  const [orgInfo, setOrgInfo] = useState<OrganizationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  
  const { handleApiError: handleTrialApiError } = useTrialExpirationHandler();
  const { handleApiError: handleSubscriptionApiError } = useSubscriptionExpirationHandler();

  const fetchOrgInfo = async () => {
    try {
      setLoading(true);
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

      const response = await axios.get(`${API_URL}/api/organization`, {
        withCredentials: true
      });
      
      setOrgInfo(response.data.organization);
    } catch (error) {
      // Check both trial and subscription expiration
      if (!handleTrialApiError(error)) {
        handleSubscriptionApiError(error);
      }
      logger.error('Error fetching organization info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.organizationId) {
      fetchOrgInfo();
    }
  }, [user]);

  return {
    orgInfo,
    loading,
    refetch: fetchOrgInfo
  };
};

export default useOrganizationInfo; 